<template>
  <v-card
    class="settings-card"
    elevation="3"
    rounded="lg"
  >
    <v-card-title class="text-h5 font-weight-medium">
      <v-icon left color="primary">mdi-truck-delivery</v-icon>
      Third Party Carriers
    </v-card-title>
    
    <v-alert
      v-if="company.data_sources.some(i => i.uses_shopify_labels)"
      type="info"
      class="mx-6 mb-4"
      elevation="2"
      border="left"
      dense
    >
      One or more of your stores are set to use Shopify labels. This section is primarily for stores that don't use Shopify Labels. You do not need to setup additional carriers. Edit settings for Shopify Labels in Stores.
    </v-alert>
    
    <v-card 
      v-for="(carrier, index) in company.carriers.filter(c => c.type !== 'shopify')"
      :key="`carrier-${index}`"
      class="ma-4 pa-4 carrier-card"
      elevation="2"
      rounded="lg"
    >
      <v-row align="center">
        <v-col class="grow">
          <span class="font-weight-medium">{{ carrier.name }}</span>
        </v-col>
        <v-col class="text-right shrink d-flex">
          <v-btn 
            color="error" 
            text 
            rounded
            @click="remove_carrier(carrier.id)"
          >
            <v-icon left>mdi-delete</v-icon>
            Remove
          </v-btn>
          <v-btn 
            color="primary" 
            text 
            rounded
            @click="edit_carrier(carrier.id)" 
            class="ml-2"
          >
            <v-icon left>mdi-pencil</v-icon>
            Edit
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-container class="text-center py-6">
      <v-dialog
        v-model="dialog"
        width="500"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            color="primary"
            rounded
            elevation="1"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon left>mdi-plus</v-icon>
            Add Carrier
          </v-btn>
        </template>
        
        <v-card rounded="lg" elevation="4" :loading="saving_carrier">
          <v-form @submit.prevent="save_carrier" ref="carrier_form">
            <v-card-title class="primary white--text">
              <v-row dense>
                <v-col class="grow">{{ editing_carrier ? 'Edit' : 'Add' }} Carrier</v-col>
                <v-col class="shrink">
                  <v-btn
                    icon
                    @click="dialog = false"
                  >
                    <v-icon color="white">mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-title>
            
            <v-card-text 
              class="pt-4"
              v-if="saving_carrier || editing_carrier || (get_plan && company.carriers.length < get_plan.max_carriers)"
            >
              <v-text-field
                rounded
                outlined
                v-model="new_carrier.name"
                label="Nickname"
                placeholder="ACME Postnord account"
                :rules="[v => !!v || 'Item is required']"
              >
              </v-text-field>
              
              <v-select
                v-model="new_carrier.type"
                @change="new_carrier = { name: new_carrier.name, type: new_carrier.type, id: new_carrier.id, ...carriers[new_carrier.type]}"
                label="Type"
                item-text="display_name"
                item-value="type"
                :items="carrier_types"
                :rules="[v => !!v || 'Item is required']"
                outlined
                rounded
              />
              
              <!-- Carrier-specific fields -->
              <template v-if="new_carrier.type === 'unifaun'">
                <v-text-field
                  rounded
                  outlined
                  v-model="new_carrier.api_key"
                  label="Combined ID"
                  placeholder="E2DRTSZ3WFUIHRSR-UKX2537DPCF2APSAEQKTHN2U"
                  @blur="trim_key"
                  :rules="[v => !!v || 'Item is required',
                          v => v && v.length === 41 || 'Wrong format. Make sure to get the entire combined id which consist of <ID 16 letters>-<SECRET ID 24 letters>']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="number"
                  v-model="new_carrier.sender_id"
                  label="Sender-ID"
                  placeholder="1"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  v-model="new_carrier.contents"
                  label="Default contents"
                  placeholder="ACME T-shirts"
                  :rules="[v => !!v || 'Item is required']"
                />
              </template>
              
              <!-- Other carrier types (fedex, dhl_parcel_de, postnord) -->
              <template v-else-if="new_carrier.type === 'fedex'">
                <!-- FedEx: the account's billing address is what FedEx validates in step 1 of the registration -->
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.account_number"
                  label="FedEx account number"
                  placeholder="123456789"
                  hint="9 digits"
                  :rules="[v => !!v && /^\d{9}$/.test(String(v).trim()) || 'A FedEx account number has 9 digits']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.contact.companyName"
                  label="Company name (as registered with FedEx)"
                  placeholder="ACME Company"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.contact.personName"
                  label="Contact person"
                  placeholder="John Doe"
                  :rules="[v => !!v || 'Item is required']"
                />
                <TelInput 
                  v-model="new_carrier.contact.phoneNumber"
                  label="Contact phone number"
                  placeholder="+46701112233"
                  required
                />
                <v-text-field
                  rounded
                  outlined
                  type="email"
                  v-model="new_carrier.contact.emailAddress"
                  label="Contact email"
                  placeholder="shipping@acme.com"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.address.streetLines[0]"
                  label="Street (billing address of the FedEx account)"
                  placeholder="Lugna gatan 1"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.address.streetLines[1]"
                  label="Street 2"
                  placeholder=""
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.address.city"
                  label="City"
                  placeholder="Stockholm"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.address.postalCode"
                  label="Postal code"
                  placeholder="12345"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.address.countryCode"
                  label="Country code (2 letters)"
                  placeholder="SE"
                  :rules="[v => !!v && /^[A-Za-z]{2}$/.test(v) || '2 letter country code']"
                  @input="new_carrier.address.countryCode = (new_carrier.address.countryCode || '').toUpperCase()"
                />
                <v-select
                  rounded
                  outlined
                  v-model="new_carrier.pickup_type"
                  :items="fedex_pickup_types"
                  label="How do parcels get to FedEx?"
                />
                <v-select
                  rounded
                  outlined
                  v-model="new_carrier.customs_document_mode"
                  :items="fedex_customs_modes"
                  label="Commercial invoice for customs"
                  hint="Both are sent electronically to FedEx (Electronic Trade Documents)"
                  persistent-hint
                  class="mb-4"
                />

                <v-alert v-if="new_carrier.eula && new_carrier.eula.accepted_at" type="success" text dense border="left" class="mb-4">
                  FedEx End User License Agreement accepted {{ new_carrier.eula.accepted_at.substring(0, 10) }}
                  <span v-if="new_carrier.eula.accepted_by">by {{ new_carrier.eula.accepted_by }}</span>
                </v-alert>

                <template v-if="new_carrier.auth && new_carrier.auth.child_key">
                  <v-alert type="success" dense elevation="2" border="left" class="mb-2">
                    <v-icon left small>mdi-check-circle</v-icon>
                    FedEx account connected
                    <v-btn text small class="ml-2" type="button" @click="reset_fedex_auth">Reconnect</v-btn>
                  </v-alert>
                </template>
                <FedExRegistration
                  v-else
                  class="mb-4"
                  :carrier="new_carrier"
                  :company_id="company.id"
                  :eula_accepted="!!(new_carrier.eula && new_carrier.eula.accepted_at)"
                  @open-eula="show_eula = true"
                  @authorized="on_fedex_authorized"
                />
                <FedExEula v-model="show_eula" @accepted="on_eula_accepted" />
              </template>
              
              <template v-else-if="new_carrier.type === 'dhl_parcel_de'">
                <!-- DHL fields -->
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.gkp_user"
                  label="DHL Business Customer Portal Username"
                  placeholder="UserName"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="password"
                  v-model="new_carrier.gkp_pass"
                  label="DHL Business Customer Portal password"
                  placeholder=""
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-checkbox
                  v-model="new_carrier.use_shop_name"
                  label="Use shop name as sender name"
                  class="mt-0"
                  ></v-checkbox>
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.shipper.name1"
                  label="Shipper Name"
                  placeholder="ACME Company"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.shipper.addressStreet"
                  label="Street Address"
                  placeholder="Lugna gatan 1"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.shipper.postalCode"
                  label="Postal Code"
                  placeholder="12345"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.shipper.city"
                  label="City"
                  placeholder="Stockholm"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.shipper.country"
                  label="Country Code (3 letters)"
                  placeholder="DEU"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="email"
                  v-model="new_carrier.shipper.email"
                  label="Email"
                  placeholder="contact@example.com"
                  :rules="[v => !!v || 'Item is required']"
                />
                <TelInput 
                  v-model="new_carrier.shipper.phone"
                  label="Phone Number"
                  placeholder="+49701112233"
                  @input="validatePhone"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="dhl_return_receivers"
                  label="Return receiver ids (for return labels)"
                  placeholder="DE=deu, AT=aut, *=deu"
                  hint="From the DHL Business Customer Portal under Returns > Settings. One per country the parcel is sent from, as COUNTRY=id, comma separated. * is the default for other countries. Leave empty if you do not offer DHL return labels."
                  persistent-hint
                  :rules="[validate_return_receivers]"
                />
              </template>
              
              <template v-else-if="new_carrier.type === 'postnord'">
                <!-- Postnord fields -->
                <v-select
                  v-model="new_carrier.issuerCode"
                  label="Postnord customer country *"
                  item-text="name"
                  item-value="id"
                  :items="[
                    {name: 'Postnord Sweden', id: 'Z12'},
                    {name: 'Postnord Denmark', id: 'Z11'},
                    {name: 'Postnord Norway', id: 'Z13'},
                    {name: 'Postnord Finland', id: 'Z14'},
                  ]"
                  :rules="[v => !!v || 'Item is required']"
                  outlined
                  rounded
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.partyIdentification.partyId"
                  label="Postnord customer number *"
                  placeholder="01234567"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.nameIdentification.name"
                  label="Name *"
                  placeholder="John Doe / ACME Company"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.nameIdentification.companyName"
                  label="Company Name"
                  placeholder="ACME Company"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.nameIdentification.careOfName"
                  label="C/O Name"
                  placeholder="Big warehouse"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.address.streets[0]"
                  label="Street *"
                  placeholder="Main road 123"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.address.streets[1]"
                  label="Street 2"
                  placeholder=""
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.address.postalCode"
                  label="Postal Code *"
                  placeholder="12345"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.address.city"
                  label="City *"
                  placeholder="Stockholm"
                  :rules="[v => !!v || 'Item is required']"
                />
                <v-select
                  v-model="new_carrier.party.address.countryCode"
                  label="Country *"
                  item-text="name"
                  item-value="id"
                  :items="[
                    {name: 'Sweden', id: 'SE'},
                    {name: 'Denmark', id: 'DK'},
                    {name: 'Norway', id: 'NO'},
                    {name: 'Finland', id: 'FI'},
                  ]"
                  :rules="[v => !!v || 'Item is required']"
                  outlined
                  rounded
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.contact.contactName"
                  label="Contact name"
                  placeholder="John Doe"
                />
                <v-text-field
                  rounded
                  outlined
                  type="text"
                  v-model="new_carrier.party.contact.emailAddress"
                  label="Contact email"
                  placeholder="john.doe@example.com"
                />
                <TelInput 
                  v-model="new_carrier.party.contact.phoneNo"
                  label="Contact phone"
                  placeholder="+4631123123"
                  @input="validatePhone"
                />
                <TelInput 
                  v-model="new_carrier.party.contact.smsNo"
                  label="Contact SMS Number"
                  placeholder="+46703123123"
                  @input="validatePhone"
                />
              </template>
            </v-card-text>
            
            <v-card-text
              v-else
              class="pt-4 text-center"
            >
              <v-alert
                type="warning"
                dense
                elevation="2"
                border="left"
              >
                Your current plan does not allow more carrier accounts to be added. Please upgrade plan.
              </v-alert>
            </v-card-text>

            <v-divider></v-divider>

            <v-card-actions class="pa-4">
              <v-spacer></v-spacer>
              <v-btn
                color="grey darken-1"
                text
                rounded
                @click="dialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                rounded
                elevation="1"
                type="submit"
                :loading="saving_carrier"
                :disabled="new_carrier.type === 'fedex' && (!new_carrier.auth || !new_carrier.auth.child_key) && !editing_carrier"
                v-if="saving_carrier || editing_carrier || (get_plan && company.carriers.length < get_plan.max_carriers)"
              >
                {{ editing_carrier ? 'Save' : 'Connect' }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
    </v-container>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { uid } from 'uid'
import TelInput from '@/components/base_components/TelInput'
import FedExRegistration from '@/components/fedex/FedExRegistration'
import FedExEula from '@/components/fedex/FedExEula'


export default {
  components: {
    TelInput,
    FedExRegistration,
    FedExEula,
  },
  data(){
    return {
      editing_carrier: false,
      dhl_return_receivers: '',
      dialog: false,
      connection_error: false,
      saving_carrier: false,
      show_eula: false,
      fedex_pickup_types: [
        { text: 'FedEx picks up on a scheduled pickup', value: 'USE_SCHEDULED_PICKUP' },
        { text: 'We drop parcels off at a FedEx location', value: 'DROPOFF_AT_FEDEX_LOCATION' },
        { text: 'We call FedEx to schedule a pickup', value: 'CONTACT_FEDEX_TO_SCHEDULE' },
      ],
      fedex_customs_modes: [
        { text: 'Our own invoice (uploaded, includes MID codes and customs names)', value: 'upload' },
        { text: 'FedEx generated commercial invoice', value: 'fedex_generated' },
      ],
      new_carrier: {
        name: '',
        type: '',
        id: '',
      },
      carriers: {
        unifaun: {
          api_key: '',
          sender_id: '',
          contents: '',
        },
        dhl_parcel_de: {
          gkp_user: '',
          gkp_pass: '',
          billing_number: '',
          use_shop_name: false,
          "shipper": {
                "name1": "",
                "addressStreet": "",
                "postalCode": "",
                "city": "",
                "country": "",
                "email": "",
                "phone": ""
              },
        },
        fedex: {
          account_number: '',
          pickup_type: 'USE_SCHEDULED_PICKUP',
          customs_document_mode: 'upload',
          eula: null,
          "contact": {
            "personName": "",
            "phoneNumber": "",
            "companyName": "",
            "emailAddress": ""
          },
          "address": {
            "streetLines": [
              "",
              ""
            ],
            "city": "",
            // "stateOrProvinceCode": "",
            "postalCode": "",
            "countryCode": ""
          }
        },
        postnord: {
          "issuerCode": "",
          "partyIdentification": {
            "partyId": "",
            "partyIdType": "160"
          },
          "party": {
            "nameIdentification": {
              "name": "",
              "companyName": "",
              "careOfName": ""
            },
            "address": {
              "streets": [
                "", ""
              ],
              "postalCode": "",
              // "placeName": "string",
              // "state": "string",
              "city": "",
              "countryCode": ""
            },
            "contact": {
              "contactName": "",
              "emailAddress": "",
              "phoneNo": "",
              "smsNo": ""
            }
          }
        },
      },
      carrier_types: [
        { display_name: 'Unifaun / nShift', type: 'unifaun' },
        { display_name: 'Postnord', type: 'postnord' },
        { display_name: 'FedEx', type: 'fedex' },
        { display_name: 'DHL Parcel DE', type: 'dhl_parcel_de' },
      ],
      // eori_number: '',
    }
  },
  computed: {
    ...mapState({
      company: state => state.warehouse.company,
    }),
    ...mapGetters({
      get_plan: 'warehouse/get_plan',
    }),
  },
  watch: {
    // new_carrier: function(carrier){
    //   if(
    //     carrier.type === 'fedex' 
    //     // && !this.editing_carrier 
    //     && carrier.address.countryCode 
    //     && carrier.address.streetLines[0]
    //     && carrier.address.postalCode
    //     && carrier.address.city
    //     && carrier.account_number
    //     && carrier.contact.companyName
    //     && !this.account_auth_token
    //   ) this.on_request_code()
    // },
    dialog: function(v){
      if(!v) {
        console.log('watcher')
        this.editing_carrier = false
        this.new_carrier = {
          name: '',
          type: '',
          id: '',
        }
        this.$refs.carrier_form.resetValidation()
        this.connection_error = false
        this.dhl_return_receivers = ''
      }
    }
  },
  methods: {
    on_eula_accepted({ accepted_at, version }){
      const profile = this.$store.state.user.userProfile || {}
      this.$set(this.new_carrier, 'eula', { accepted_at, version, accepted_by: profile.email || profile.id || null })
    },
    on_fedex_authorized(auth){
      // Child credentials issued by FedEx for this account; all shipping on the account uses them
      this.$set(this.new_carrier, 'auth', auth)
      this.$delete(this.new_carrier, 'access_token')
      this.$delete(this.new_carrier, 'access_token_expires_at')
      // The account is connected, so save the carrier straight away instead of asking for another click
      this.$nextTick(() => this.save_carrier())
    },
    reset_fedex_auth(){
      this.$delete(this.new_carrier, 'auth')
      this.$delete(this.new_carrier, 'access_token')
      this.$delete(this.new_carrier, 'access_token_expires_at')
    },
    trim_key(){
      this.new_carrier.api_key = this.new_carrier.api_key.replace(/ /g, '')
    },
    edit_carrier(id){
      const carrier = this.company.carriers.find(c => c.id === id)
      this.editing_carrier = true
      this.new_carrier = carrier.type === 'fedex'
        ? { ...this.carriers.fedex, ...carrier, contact: { ...this.carriers.fedex.contact, ...(carrier.contact || {}) } }
        : { ...carrier }
      this.dhl_return_receivers = Object.entries(carrier.return_receiver_ids || {}).map(([country, id]) => `${country}=${id}`).join(', ')
      this.dialog = true
    },
    // "DE=deu, AT=aut, *=deu" -> { DE: 'deu', AT: 'aut', '*': 'deu' }
    parse_return_receivers(text){
      return String(text || '').split(/[,;\n]+/).map(part => part.trim()).filter(Boolean).reduce((tot, part) => {
        const [country, id] = part.split('=').map(v => v.trim())
        if(country && id) tot[country === '*' ? '*' : country.toUpperCase()] = id
        return tot
      }, {})
    },
    validate_return_receivers(text){
      const parts = String(text || '').split(/[,;\n]+/).map(part => part.trim()).filter(Boolean)
      return parts.every(part => /^(\*|[A-Za-z]{2})\s*=\s*\S+$/.test(part)) || 'Use COUNTRY=id, for example DE=deu, AT=aut'
    },
    async remove_carrier(id){
      console.log(id);
      const is_used = this.company.data_sources
        .filter(ds => ds.shipping_map
          .some(s => s.sf_carrier_account_id === id)
          )
        .map(ds => ds.name)
      if(is_used.length) return this.$store.commit('app/SET_SNACK_BAR', `This carrier account is being used in shipping maps for ${is_used.toString()}. Remove these shipping routings before removing the carrier account.`)
      const { ok } = await this.$prompt({
        text: 'Are you sure? This action can not be undone.',
        buttons: [
          {
            value: { ok: true },
            text: 'Proceed'
          },
          {
            value: { ok: false },
            text: 'cancel'
          },
        ]
      })
      if(!ok) return 
      const carriers = this.company.carriers
      const index = carriers.findIndex(c => c.id === id)
      if(index === -1) return 
      carriers.splice(index, 1)
      this.$store.commit('warehouse/SET_COMPANY', { carriers })
      this.$store.dispatch('warehouse/save_company', ['carriers'])
    },
    async save_carrier(){
      if(!this.$refs.carrier_form.validate()) return this.$store.commit('app/SET_SNACK_BAR', 'Something is not properly filled out, please scroll up and make sure everything seems correct!')
      this.saving_carrier = true
      // if(this.eori_number) this.update_data_source({ eori_number: this.eori_number })
      const carriers = this.company.carriers
      const id = this.new_carrier.id || uid()
      if(this.new_carrier.type === 'dhl_parcel_de') this.new_carrier.return_receiver_ids = this.parse_return_receivers(this.dhl_return_receivers)
      if(this.new_carrier.id){ carriers[carriers.findIndex(c => c.id === id)] = { ...this.new_carrier } }
      else carriers.push({ ...this.new_carrier, id })
      this.$store.commit('warehouse/SET_COMPANY', { carriers })
      await this.$store.dispatch('warehouse/save_company', ['carriers'])
      
      await this.$store.dispatch('shipping/get_carrier_partners', {carrier_id: id})
        .then(() => {
          this.saving_carrier = false
          return this.dialog = false
          })
        .catch(async () => {
          this.saving_carrier = false
          this.$store.commit('app/SET_SNACK_BAR', 'Failed to connect. Please check credentials.')
          const carriers = this.company.carriers.filter(c => c.id !== id)
          this.$store.commit('warehouse/SET_COMPANY', { carriers })
          await this.$store.dispatch('warehouse/save_company', ['carriers'])
        })
    },
    // update_data_source(set){
    //   this.$store.dispatch('warehouse/save_data_source', {
    //     id: this.company.data_sources[0].id,
    //     ...set
    //   })
    // },
    // Phone validation methods
    
    validatePhone(phoneNumber) {
      // Allow empty values (when user clears the field)
      if (!phoneNumber) {
        this.new_carrier.shipper.phone = null;
        return;
      }
      
      if (phoneNumber) {
        if (!phoneNumber.startsWith('+')) {
          this.new_carrier.shipper.phone = '+' + phoneNumber;
        } else {
          this.new_carrier.shipper.phone = phoneNumber;
        }
        
        this.new_carrier.shipper.phone = this.new_carrier.shipper.phone
          .replace(/\s+/g, '')
          .replace(/[().-]/g, '');
      }
    },
    
  },
  mounted(){
    if(this.$route.query.showDialog) this.dialog = true
    else this.dialog = false
  }
}
</script>

<style lang="scss" scoped>
.settings-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
  animation: fadeIn 0.8s ease-out;
  margin-bottom: 24px;
  
  // &:hover {
  //   transform: translateY(-5px);
  //   box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1) !important;
  // }
  
  .v-card__title {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    padding-bottom: 16px;
  }
  
  .v-card__text {
    padding-top: 16px;
  }
}

.carrier-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 8px !important;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.08) !important;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
